/**
 * Hero Cache - Instant LCP by caching hero data in localStorage
 * 
 * Strategy:
 * 1. On first load: Show static fallback from heroConfig.ts (matches index.html)
 * 2. On subsequent loads: Immediately show cached data (instant LCP)
 * 3. Background refresh: Update cache after showing cached data
 */

import { FIRST_HERO } from './heroConfig';

// Re-export the first hero from config for backward compatibility
export const FIRST_HERO_FALLBACK = FIRST_HERO;

// Cache key and expiration
const HEROES_CACHE_KEY = 'buxbox-heroes-cache';
const CACHE_MAX_AGE = 30 * 60 * 1000; // 30 minutes

export type CachedHero = typeof FIRST_HERO_FALLBACK;

interface HeroCache {
  heroes: CachedHero[];
  timestamp: number;
  version: number;
}

const CACHE_VERSION = 4; // Simplified - no lite_mode or preview_image_url

/**
 * Get cached heroes from localStorage
 * Returns null if cache is missing, expired, or invalid
 */
export function getCachedHeroes(): CachedHero[] | null {
  try {
    const cached = localStorage.getItem(HEROES_CACHE_KEY);
    if (!cached) return null;
    
    const data: HeroCache = JSON.parse(cached);
    
    // Check version
    if (data.version !== CACHE_VERSION) {
      localStorage.removeItem(HEROES_CACHE_KEY);
      return null;
    }
    
    // Check expiration
    if (Date.now() - data.timestamp > CACHE_MAX_AGE) {
      // Don't remove - still usable as stale data, just needs refresh
      return data.heroes;
    }
    
    return data.heroes;
  } catch (e) {
    console.warn('Failed to read hero cache:', e);
    return null;
  }
}

/**
 * Save heroes to localStorage cache
 */
export function setCachedHeroes(heroes: CachedHero[]): void {
  try {
    const cache: HeroCache = {
      heroes,
      timestamp: Date.now(),
      version: CACHE_VERSION
    };
    localStorage.setItem(HEROES_CACHE_KEY, JSON.stringify(cache));
  } catch (e) {
    console.warn('Failed to write hero cache:', e);
  }
}

/**
 * Check if cache is stale (older than max age)
 */
export function isCacheStale(): boolean {
  try {
    const cached = localStorage.getItem(HEROES_CACHE_KEY);
    if (!cached) return true;
    
    const data: HeroCache = JSON.parse(cached);
    return Date.now() - data.timestamp > CACHE_MAX_AGE;
  } catch {
    return true;
  }
}

/**
 * Clear the hero cache
 */
export function clearHeroCache(): void {
  try {
    localStorage.removeItem(HEROES_CACHE_KEY);
  } catch (e) {
    console.warn('Failed to clear hero cache:', e);
  }
}

/**
 * Get instant initial heroes - returns cached data or fallback
 * This should be called synchronously for instant LCP
 */
export function getInstantHeroes(): CachedHero[] {
  const cached = getCachedHeroes();
  if (cached && cached.length > 0) {
    return cached;
  }
  // Return hardcoded fallback for absolute fastest paint
  return [FIRST_HERO_FALLBACK];
}
